// ---------------------------------------------------------------------------
// LA DÉPENSE — qui paie quoi pour ouvrir une séance
//
// Toji, 14/09 : « je ne suis pas fan de l'idée que n'importe qui puisse
// utiliser mon système […] Si jamais ils sont plusieurs en vocal, ils peuvent à
// la limite dépenser des points pour lancer la prochaine session. » Puis :
// « on peut soit choisir pour un épisode, soit choisir une durée […] on peut
// payer tant et on fait des discounts pour des grandes sessions […] s'il y a un
// rush ou quoi, les gens peuvent l'économiser. Comme ça, ils déclenchent tout
// dans une soirée. »
//
// ⚠️ DEUX RÈGLES CONTRE-INTUITIVES, TOUTES DEUX VOULUES :
//
//  1. Être PLUSIEURS coûte MOINS cher : le prix est fixe et se PARTAGE entre
//     les présents. Trois personnes, c'est dix points chacune au lieu de trente.
//     Un système fait pour regarder ensemble serait absurde s'il rendait le
//     groupe plus coûteux que la solitude.
//
//  2. Acheter GROS coûte moins cher à l'unité. C'est ce qui donne un sens à
//     l'épargne : garder ses points pour une soirée entière doit valoir mieux
//     que les dépenser un épisode à la fois, sinon personne n'attend jamais et
//     la « grande session » n'existe pas.
//
// Ce module ne lit ni n'écrit aucun solde : il reçoit les soldes, rend une
// RÉPARTITION, et c'est l'appelant qui débite. Une fonction qui calcule et
// débite à la fois ne peut pas être demandée « pour voir » — or c'est
// exactement ce que fait le bot avant d'afficher « il vous manque 12 points ».
//
// ⚠️ LE SEUL IMPORT DE CE MODULE, ET IL EST VOULU (défaut C1, 14/09).
// `prixUnitaireDe` est l'ARBITRE écrit du prix d'une unité de carnet : « un
// `prixUnitaire` explicite et strictement positif gagne, TOUJOURS ». Tant que
// ce module en gardait sa propre copie, la caisse et l'écran facturaient deux
// nombres différents sur le même barème (écart ×4 mesuré). Le `COPY` du
// Dockerfile (l.51 et l.68) emporte déjà `reglages-points.js` À CÔTÉ de ce
// fichier : l'import ne casse pas le partage avec le lecteur, et
// `apps/player/essais/contrat-partage-avec-le-bot.mjs` suit la fermeture
// transitive des imports pour le prouver.
// ---------------------------------------------------------------------------

import { prixUnitaireDe } from './reglages-points.js';

/** Le prix d'une séance à l'unité, par nature de contenu. Valeurs par défaut. */
export const PRIX = { episode: 30, film: 60 };

/** Le prix d'un booster : la moitié, arrondie vers le haut. Jamais la gratuité. */
export const REMISE_BOOSTER = 0.5;

/** Combien de minutes une tranche couvre, en facturation à la durée. */
export const MINUTES_PAR_TRANCHE = 60;

/** Les raisons d'un refus, pour que l'appelant n'ait pas à lire un texte. */
export const REFUS = {
  SALON_OCCUPE: 'salon-occupe',
  PERSONNE: 'personne-en-vocal',
  PAS_ASSEZ: 'pas-assez-de-points',
};

/**
 * La remise de volume qui s'applique à une quantité, en pourcent.
 *
 * Les paliers sont `[{ quantite, remisePourcent }]` — le plus grand palier
 * atteint gagne. Ils ne se cumulent pas : trois paliers de 10 % ne font pas
 * 30 %, sinon une liste un peu longue offrirait la gratuité sans que personne
 * ne l'ait décidé en remplissant le formulaire.
 *
 * Sans palier, aucune remise : un serveur qui n'a rien réglé facture au prix.
 */
export function remiseDuVolume(quantite, paliers = []) {
  const n = Math.max(1, Math.floor(Number(quantite) || 1));
  let meilleure = 0;
  for (const p of paliers ?? []) {
    const seuil = Math.floor(Number(p?.quantite) || 0);
    const remise = Number(p?.remisePourcent);
    if (seuil <= 0 || !Number.isFinite(remise) || remise <= 0) continue;
    if (n >= seuil && remise > meilleure) meilleure = Math.min(100, remise);
  }
  return meilleure;
}

/**
 * Combien de tranches couvrent une durée, en facturation à la durée.
 *
 * Arrondi vers le HAUT : une soirée de 70 minutes consomme deux tranches d'une
 * heure. Arrondir vers le bas offrirait la fin de chaque séance, et toutes les
 * séances finiraient par déborder de dix minutes.
 */
export function tranchesPour(minutes, minutesParTranche = MINUTES_PAR_TRANCHE) {
  const m = Number(minutes);
  const t = Math.max(1, Math.floor(Number(minutesParTranche) || MINUTES_PAR_TRANCHE));
  if (!Number.isFinite(m) || m <= 0) return 1;
  return Math.max(1, Math.ceil(m / t));
}

/**
 * Le prix total d'un lot, remises comprises, et le détail de son calcul.
 *
 * L'ordre des remises n'est pas indifférent : le VOLUME s'applique d'abord (il
 * porte sur ce qu'on achète), le BOOST ensuite (il porte sur qui achète). Dans
 * l'autre sens, un booster verrait sa remise rognée par celle du volume, alors
 * que les deux lui sont dues à des titres différents.
 *
 * Le détail est rendu parce que l'embed l'affiche : « 5 épisodes × 30 = 150,
 * −20 % volume = 120, −25 % boost = 90 ». Un prix sans son calcul se discute ;
 * un prix qui se montre ne se discute pas.
 */
export function prixDuLot({
  nature = 'episode',
  quantite = 1,
  minutes = null,
  participants = 1,
  boosters = 0,
  reglages = null,
} = {}) {
  const r = reglages ?? {};
  const mode = r.mode === 'duree' ? 'duree' : 'episode';

  const unites = mode === 'duree'
    ? tranchesPour(minutes ?? quantite * (r.minutesParTranche ?? MINUTES_PAR_TRANCHE), r.minutesParTranche)
    : Math.max(1, Math.floor(Number(quantite) || 1));

  const prixUnitaire = mode === 'duree'
    ? Math.max(0, Math.floor(Number(r.prixTranche ?? PRIX.episode)))
    : Math.max(0, Math.floor(Number(
      nature === 'film' ? (r.prixFilm ?? PRIX.film) : (r.prixEpisode ?? PRIX.episode),
    )));

  const base = prixUnitaire * unites;

  const volume = remiseDuVolume(unites, r.paliers);
  const apresVolume = Math.ceil(base * (1 - volume / 100));

  // La remise de boost porte sur la PART des boosters, pas sur le prix entier :
  // un booster parmi trois ne doit pas faire moitié prix à ses deux voisins.
  const nbP = Math.max(1, Number(participants) || 1);
  const nbB = Math.max(0, Math.min(Number(boosters) || 0, nbP));
  const tauxBoost = (Number.isFinite(Number(r.remiseBoosterPourcent))
    ? Number(r.remiseBoosterPourcent) / 100 : REMISE_BOOSTER);
  const partBoostee = nbB / nbP;
  const prix = Math.ceil(apresVolume * (1 - partBoostee * tauxBoost));

  return {
    prix,
    mode,
    unites,
    prixUnitaire,
    base,
    remiseVolume: volume,
    apresVolume,
    boosters: nbB,
    remiseBoost: Math.round(partBoostee * tauxBoost * 100),
    minutesParTranche: mode === 'duree'
      ? Math.max(1, Math.floor(Number(r.minutesParTranche) || MINUTES_PAR_TRANCHE)) : null,
  };
}

/** Le prix demandé à ce groupe pour UNE unité. Raccourci historique de `prixDuLot`. */
export function prixDe(nature, { boosters = 0, participants = 1, reglages = null } = {}) {
  return prixDuLot({ nature, quantite: 1, boosters, participants, reglages }).prix;
}

/**
 * La part égale de chacun, en entiers qui retombent EXACTEMENT sur le prix.
 *
 * Un simple `Math.ceil(prix / n)` fait payer 10 × 3 = 30 pour 30, mais 11 × 3 =
 * 33 pour 31 : trois points pris à personne et rendus à personne. Ici les
 * `prix % n` premiers paient une unité de plus, et la somme est juste.
 */
export function partsEgales(prix, ids = []) {
  const n = ids.length;
  const parts = new Map();
  if (n <= 0 || prix <= 0) return parts;
  const base = Math.floor(prix / n);
  const reste = prix % n;
  // Trié : deux appels avec les mêmes gens donnent la même répartition, donc un
  // message d'erreur reproductible et un banc d'essai qui ne clignote pas.
  const ordre = [...ids].map(String).sort();
  ordre.forEach((id, i) => parts.set(id, base + (i < reste ? 1 : 0)));
  return parts;
}

/**
 * Qui paie combien, quand tout le monde n'a pas de quoi payer sa part.
 *
 * Le manque de l'un est repris par ceux qui ont du rab, à hauteur de ce qu'ils
 * ont — et seulement à cette hauteur. La solidarité s'arrête au solde, jamais à
 * une dette : le document de conception l'écrit, on ne descend jamais sous zéro.
 *
 * Rend `null` si le groupe entier n'a pas le prix ; l'appelant sait alors qu'il
 * doit annoncer le manque, pas une répartition partielle.
 */
export function repartir(prix, soldes = new Map()) {
  const ids = [...soldes.keys()].map(String);
  const total = ids.reduce((s, id) => s + Math.max(0, Number(soldes.get(id)) || 0), 0);
  if (prix <= 0) return new Map(ids.map((id) => [id, 0]));
  if (total < prix) return null;

  const du = partsEgales(prix, ids);
  const paye = new Map();
  let manque = 0;

  // Premier tour : chacun paie sa part, ou tout ce qu'il a.
  for (const id of [...du.keys()]) {
    const solde = Math.max(0, Number(soldes.get(id)) || 0);
    const part = du.get(id) ?? 0;
    const verse = Math.min(part, solde);
    paye.set(id, verse);
    manque += part - verse;
  }

  // Second tour : le manque va à ceux qui peuvent encore. Trié par capacité
  // restante décroissante — celui qui a le plus donné le moins péniblement.
  if (manque > 0) {
    const capables = [...paye.keys()]
      .map((id) => ({ id, reste: Math.max(0, Number(soldes.get(id)) || 0) - (paye.get(id) ?? 0) }))
      .filter((x) => x.reste > 0)
      .sort((a, b) => b.reste - a.reste || a.id.localeCompare(b.id));
    for (const { id, reste } of capables) {
      if (manque <= 0) break;
      const rajout = Math.min(reste, manque);
      paye.set(id, (paye.get(id) ?? 0) + rajout);
      manque -= rajout;
    }
  }

  // `total >= prix` garantit que le manque est retombé à zéro ; si ce n'était
  // pas le cas, ce serait un défaut de ce calcul et non un refus légitime — on
  // le dit plutôt que de débiter une somme qui n'ouvre rien.
  if (manque > 0) throw new Error(`répartition incohérente : ${manque} point(s) non couverts alors que le groupe en a ${total}`);
  return paye;
}

/**
 * La décision complète : cette séance peut-elle s'ouvrir, et aux frais de qui ?
 *
 * - `participants` : les identifiants dans le salon vocal (ceux de `vocal.js`).
 * - `soldes` : Map identifiant → points. Un absent vaut zéro.
 * - `quantite` / `minutes` : la taille du lot — un épisode, ou une soirée.
 * - `boosters`, `proprietaires` : des Set d'identifiants.
 * - `salonsOuverts` : les salons où une séance tourne déjà.
 * - `reglages` : la section « points » d'`access.json` (voir `reglages-points.js`).
 *
 * ⚠️ Le demandeur n'est PAS forcément le payeur unique : c'est tout le salon
 * qui paie. Demander, ici, ce n'est pas s'engager seul — c'est proposer au
 * groupe, et le groupe a déjà accepté en étant là.
 */
export function ouvrirUneSeance({
  demandeur,
  salon,
  nature = 'episode',
  quantite = 1,
  minutes = null,
  participants = [],
  soldes = new Map(),
  boosters = new Set(),
  proprietaires = new Set(),
  salonsOuverts = new Set(),
  reglages = null,
} = {}) {
  const qui = String(demandeur ?? '');
  const ou = String(salon ?? '');

  // Un propriétaire ouvre sans payer : c'est « l'accord d'un admin ». Il passe
  // AVANT le verrou de salon, parce qu'un exploitant doit pouvoir reprendre la
  // main sur une séance coincée sans avoir à la fermer d'abord.
  if (proprietaires.has(qui)) {
    return { ok: true, gratuit: true, motif: 'proprietaire', prix: 0, repartition: new Map() };
  }

  // L'économie coupée rend le lecteur libre, comme avant qu'elle existe. C'est
  // l'interrupteur que Toji garde sous la main si le barème se révèle mauvais
  // un soir de rush — mieux vaut une soirée gratuite qu'une soirée bloquée.
  if (reglages && reglages.actif === false) {
    return { ok: true, gratuit: true, motif: 'economie-desactivee', prix: 0, repartition: new Map() };
  }

  if (ou && salonsOuverts.has(ou)) {
    return { ok: false, refus: REFUS.SALON_OCCUPE, prix: 0 };
  }

  const presents = [...new Set(participants.map(String).filter(Boolean))];
  if (!presents.length) {
    return { ok: false, refus: REFUS.PERSONNE, prix: 0 };
  }

  const nbBoosters = presents.filter((id) => boosters.has(id)).length;
  const facture = prixDuLot({
    nature, quantite, minutes, participants: presents.length, boosters: nbBoosters, reglages,
  });

  const disponibles = new Map(presents.map((id) => [id, Math.max(0, Number(soldes.get(id)) || 0)]));
  const repartition = repartir(facture.prix, disponibles);

  if (!repartition) {
    const total = [...disponibles.values()].reduce((s, v) => s + v, 0);
    const parMinute = Math.max(1, Number(reglages?.parMinuteVocale) || 1);
    return {
      ok: false,
      refus: REFUS.PAS_ASSEZ,
      prix: facture.prix,
      facture,
      total,
      manque: facture.prix - total,
      // Le temps de vocal qu'il reste à donner : c'est la seule forme sous
      // laquelle le manque veut dire quelque chose à quelqu'un.
      minutes: Math.ceil((facture.prix - total) / parMinute),
    };
  }

  return {
    ok: true,
    gratuit: false,
    motif: nbBoosters > 0 ? 'boost' : 'points',
    prix: facture.prix,
    facture,
    boosters: nbBoosters,
    participants: presents.length,
    repartition,
  };
}

// ---------------------------------------------------------------------------
// LE CARNET — L'ARITHMÉTIQUE PURE
//
// Méthodologie « Le Carnet Scellé », §1.7 et §3. Quatre fonctions sans Redis,
// sans réseau, sans état : elles reçoivent un barème GELÉ et rendent un nombre.
// C'est ce qui les rend éprouvables exhaustivement — `essais/zz-economie.mjs`
// les passe sur tous les couples (acheté, gardé) de 1 à 25.
//
// LA RÈGLE UNIQUE, dont tout le reste découle (§1.2) :
//
//   « Un carnet vaut toujours `prixDuLot(unités qu'il contient, barème gelé)`.
//     Toute opération recalcule cette valeur et ne fait circuler que la
//     différence. »
//
// Il n'y a donc qu'UNE opération d'argent : porter le carnet à N unités.
// Acheter, racheter, reprendre au tarif du jour, rembourser partiellement,
// c'est le même verbe ; seul le signe de la différence décide si l'on débite
// ou si l'on rend.
//
// ⚠️ Rien ici ne relit un barème au présent. Les paliers COURANTS n'entrent que
// par l'appelant, qui construit `{ ...gelé, paliers: courants }` (F5). Le prix
// unitaire, les boosters et les participants restent ceux de la vente — sinon
// une baisse de tarif ou un mois de boost achète rétroactivement le stock.
// ---------------------------------------------------------------------------

/** Ramène une valeur entre deux bornes. Écrite une fois pour ne pas l'oublier. */
function borne(valeur, mini, maxi) {
  return Math.min(maxi, Math.max(mini, valeur));
}

/** Un entier strictement positif, ou le défaut. Aucun `NaN` ne sort d'ici. */
function entierPositif(valeur, defaut = 0) {
  const n = Math.floor(Number(valeur));
  return Number.isFinite(n) && n > 0 ? n : defaut;
}

/**
 * LE BARÈME GELÉ, OU RIEN — jamais un barème « à peu près ».
 *
 * ⚠️ DÉFAUT PAYÉ, et il ne se voit sur aucun barème sain. §1.3 range le barème
 * gelé dans un champ de HASH Redis, donc en JSON : `HGET` rend une CHAÎNE. Le
 * jour où un appelant oublie son `JSON.parse` — ou le fait sur la mauvaise
 * branche —, la chaîne `'{"prixEpisode":45,...}'` arrive ici, l'ancien
 * `bareme && typeof bareme === 'object' ? bareme : {}` la remplaçait par un
 * objet VIDE, et le carnet se retrouvait facturé aux défauts du module : 30
 * l'unité au lieu de 45, aucun palier, aucun boost gelé. Rien ne levait, rien
 * ne valait `NaN`, le chiffre rendu était parfaitement plausible — et c'est
 * précisément ce qui le rendait indétectable. Sur le cas de Toji, une reprise
 * calculée ainsi rend 75 points qui ne sont dus à personne.
 *
 * On lève donc sur TOUT ce qui n'est pas un objet, sauf `null`/`undefined` :
 * ceux-là veulent dire « pas de barème », se comportent exactement comme
 * avant (les défauts du module), et c'est ce dont vivent l'aperçu de carte et
 * les appels sans argument. Un barème ABSENT est une intention ; un barème
 * MAL FORMÉ est un accident, et un accident sur un chemin d'argent se dit.
 */
function baremeLisible(bareme) {
  if (bareme === null || bareme === undefined) return {};
  if (typeof bareme !== 'object') {
    throw new Error(`barème de carnet illisible : ${typeof bareme} au lieu d'un objet (un JSON non analysé ?)`);
  }
  return bareme;
}

/**
 * Le prix d'UNE unité sous un barème donné — exactement la dérivation que fait
 * `prixDuLot`, recopiée volontairement plutôt qu'extraite.
 *
 * Pourquoi la recopier : `prixDuLot` est le contrat public gelé par
 * `essais/economie.mjs` et par les cartes d'achat. La refactoriser pour
 * partager trois lignes ferait dépendre l'affichage d'un lot d'une fonction
 * neuve, et un défaut ici casserait une carte qui marche depuis toujours. Le
 * banc compare les deux valeurs sur tout le domaine : la copie est surveillée.
 */
function prixUnitaireDuBareme(nature, bareme) {
  const r = bareme && typeof bareme === 'object' ? bareme : {};
  const mode = r.mode === 'duree' ? 'duree' : 'episode';
  const brut = mode === 'duree'
    ? (r.prixTranche ?? PRIX.episode)
    : (nature === 'film' ? (r.prixFilm ?? PRIX.film) : (r.prixEpisode ?? PRIX.episode));
  const n = Math.floor(Number(brut));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/**
 * Le barème tel qu'on le passe à `prixDuLot`, complété et assaini.
 *
 * Deux défauts payés, tous deux invisibles sur un barème sain :
 *
 *  1. `minutesParTranche` à 0. En mode durée, `prixDuLot` calcule les minutes
 *     par `quantite * (r.minutesParTranche ?? 60)` puis les redivise par une
 *     tranche re-bornée à 1 minimum. Avec 0, le produit fait 0 et
 *     `tranchesPour` rend 1 : un carnet de douze tranches vaudrait UNE tranche.
 *     On normalise la tranche AVANT de la donner, pour que la multiplication et
 *     la division emploient le même nombre.
 *
 *  2. Un barème qui ne porte que `prixUnitaire`. §3.2 écrit littéralement
 *     `prixDuLot({ quantite: 10, prixUnitaire: 30 ← GELÉ, paliers: courants })`
 *     — or `prixDuLot` ne lit jamais `prixUnitaire`, il le DÉRIVE. On remplit
 *     donc le champ qu'il lira.
 *
 * ⚠️ DÉFAUT C1, PAYÉ LE 14/09 — DEUX RÈGLES OPPOSÉES, ÉCART ×4.
 *
 * Cette fonction posait le prix gelé « seulement s'il manque » ; le pont
 * (`donnees-membre.baremeAvecPrixGele`) le faisait GAGNER. Or
 * `reglages-points.baremeDe` émet TOUJOURS les neuf champs, `prixEpisode`
 * compris : la condition « s'il manque » n'était donc JAMAIS vraie sur un vrai
 * carnet, et les deux chemins répondaient deux nombres différents sur le même
 * barème.
 *
 *     bareme { prixEpisode: 99, prixUnitaire: 25 }, carnet de 4 unités
 *       pont   valeurAujourdhui = 100
 *       caisse valeurDuCarnet   = 396
 *
 * L'écran annonçait un prix que la caisse ne pratiquait pas. Et ça ne se voyait
 * sur aucun banc, parce que les bancs écrivent des barèmes PARTIELS — où le
 * champ manque effectivement, donc où l'ancienne règle avait l'air juste.
 *
 * ⚠️ IL N'Y A PLUS DE RÈGLE ICI, ET IL NE DOIT PAS Y EN AVOIR.
 * L'arbitre est `reglages-points.prixUnitaireDe` — « un `prixUnitaire`
 * explicite et strictement positif gagne, TOUJOURS », et c'est aussi lui qui
 * résout le `0` sentinelle (« suis la nature vendue »), le mode `duree` et les
 * bornes. Quiconque réécrit ici un `if (prixUnitaire > 0)` rouvre C1 : deux
 * copies d'une règle, c'est une règle et un futur désaccord.
 *
 * On écrase donc le champ SANS CONDITION. C'est la traduction exacte de
 * l'arbitrage : le barème gelé du carnet ne complète pas le tarif du jour, il
 * le remplace — sinon une baisse de `prixEpisode` de 30 à 10 distribuerait
 * 220 points par détenteur à la première reprise (F5).
 */
function baremePourPrix(nature, bareme) {
  const r = bareme && typeof bareme === 'object' ? bareme : {};
  const mode = r.mode === 'duree' ? 'duree' : 'episode';
  const out = { ...r, mode };
  out.minutesParTranche = entierPositif(r.minutesParTranche, MINUTES_PAR_TRANCHE);

  // ⚠️ LE GARDE-FOU F16 EST REMONTÉ ICI, ET IL DOIT Y RESTER.
  //
  // Avant C1, un `prixEpisode: 'trente'` traversait `prixDuLot` en `NaN` et
  // `valeurDuCarnet` levait sur `!Number.isFinite(prix)`. `prixUnitaireDe`, lui,
  // « rend toujours un entier ≥ 0, quoi qu'on lui donne » — c'est son contrat,
  // et il est bon là où il vit (un diviseur qui vaut `NaN` fabrique des unités
  // fantômes). Mais appliqué SANS CE GARDE, il aurait remplacé le prix illisible
  // par le défaut du module : le carnet se serait facturé 30 l'unité au lieu de
  // lever, et le chiffre rendu aurait été parfaitement plausible — exactement la
  // panne que `baremeLisible` décrit et que F16 interdit.
  //
  // On lève donc AVANT de déléguer, et seulement sur le champ que la caisse
  // lisait déjà : présent, non nul, et pas un nombre fini.
  const champ = mode === 'duree' ? 'prixTranche' : (nature === 'film' ? 'prixFilm' : 'prixEpisode');
  const brut = r[champ];
  if (brut !== undefined && brut !== null && !Number.isFinite(Number(brut))) {
    throw new Error(`barème de carnet illisible : ${champ} = ${JSON.stringify(brut)}`);
  }

  // `nature` est passée explicitement plutôt que relue dans `r` : le pont la
  // résout depuis le CARNET (`c.nature ?? bareme.nature ?? reglages.nature`) et
  // elle peut donc démentir celle du barème. L'arbitre doit trancher sur la
  // nature réellement vendue, pas sur celle qui traîne dans la copie gelée.
  const unitaire = prixUnitaireDe({ ...r, mode, nature });
  if (mode === 'duree') out.prixTranche = unitaire;
  else if (nature === 'film') out.prixFilm = unitaire;
  else out.prixEpisode = unitaire;

  return out;
}

/**
 * CE QUE VAUT UN CARNET DE N UNITÉS, sous son barème gelé.
 *
 *   valeurDuCarnet(n, bareme) = n <= 0 ? 0 : prixDuLot(n, bareme).prix
 *
 * Le carnet vide vaut zéro, et c'est le cas qui compte : `renduPour` s'en sert
 * pour dire « rendre tout te rend tout ». Un `prixDuLot({ quantite: 0 })`
 * rendrait le prix d'UNE unité (le `Math.max(1, …)` de la ligne 107), donc
 * rendre la totalité d'un carnet en retiendrait silencieusement une.
 *
 * ⚠️ Un barème corrompu LÈVE au lieu de rendre `NaN`. Un `NaN` qui descend
 * jusqu'au `HSET paye` est un carnet dont plus aucune opération ne se calcule,
 * et personne ne sait quand c'est arrivé (F16 : toute donnée de prix est un
 * entier fini, jamais `NaN`, jamais `null`). Lever ne déplace pas d'argent ;
 * rendre `0` en vendrait pour rien.
 */
export function valeurDuCarnet(n, bareme = null) {
  const unites = Math.floor(Number(n));
  if (!Number.isFinite(unites) || unites <= 0) return 0;

  const r = baremeLisible(bareme);
  const nature = r.nature === 'film' ? 'film' : 'episode';
  const { prix } = prixDuLot({
    nature,
    quantite: unites,
    minutes: null,
    // Gelés avec la vente : le statut de booster est volatile et ne figure dans
    // aucune empreinte, on ne le relit jamais au présent (F6).
    participants: r.participants ?? 1,
    boosters: r.boosters ?? 0,
    reglages: baremePourPrix(nature, r),
  });

  if (!Number.isFinite(prix)) {
    throw new Error(`barème de carnet illisible : prix non fini pour ${unites} unité(s)`);
  }
  return Math.max(0, Math.floor(prix));
}

/**
 * ⚠️ L'INVARIANT DU CARNET — ET POURQUOI C'EST UNE FONCTION, PAS UN COMMENTAIRE.
 *
 * DÉFAUT PAYÉ (C2, reproduit sur la table du §3.2 : 30 l'unité, palier 10 →
 * −25 %, carnet de 12 payé 270) :
 *
 *     rendre 3 d'un coup      →   0     (juste : neuf unités valent 270)
 *     rendre 1 trois fois     →  44     (paye tombé à 226 pour neuf unités
 *                                        qui en valent 270)
 *
 * Quarante-quatre points nés d'un découpage. La cause n'était pas la formule :
 * c'était que `paye` était traité comme un CHAMP LIBRE, que l'appelant
 * décrémentait du rendu. La seconde borne se comparait alors à un `paye`
 * dérivé, plus à la valeur du carnet — et F4 se rouvrait en trois clics.
 *
 * §1.2 ne dit pas cela : « Un carnet vaut TOUJOURS `prixDuLot(unités qu'il
 * contient, barème gelé)`. Toute opération recalcule cette valeur et ne fait
 * circuler que la différence. » `paye` est donc un INVARIANT, pas une mémoire
 * d'encaissement : après toute opération il vaut `valeurDuCarnet(unites)`.
 *
 * Un carnet dont `paye` ne vaut pas sa valeur est un carnet ABÎMÉ. Continuer à
 * calculer dessus ne produit pas une erreur visible : ça produit un nombre
 * plausible, et c'est exactement ce qui a laissé passer les 44 points. On
 * REFUSE donc l'opération plutôt que de propager l'abîmage.
 *
 * ⚠️ Un barème illisible LÈVE ici aussi (par `valeurDuCarnet`) : « incohérent »
 * et « illisible » sont deux accidents différents et doivent se dire
 * différemment, sinon un `JSON.parse` oublié se lit « carnet abîmé » et on
 * cherche la panne du mauvais côté.
 */
export function carnetCoherent({ unites = 0, paye = 0, bareme = null } = {}) {
  const p = Math.floor(Number(paye));
  if (!Number.isFinite(p)) return false;
  return p === valeurDuCarnet(unites, bareme);
}

/**
 * La même chose, mais qui REFUSE au lieu de rendre `false`, et qui rend la
 * valeur au passage — les deux appelants du module en ont besoin ensemble.
 */
function exigerUnCarnetSain(carnet, verbe) {
  const { unites = 0, paye = 0, bareme = null } = carnet ?? {};
  const valeur = valeurDuCarnet(unites, bareme);
  const p = Math.floor(Number(paye));
  if (!Number.isFinite(p) || p !== valeur) {
    throw new Error(
      `carnet incohérent : ${verbe} refusé — paye=${paye} pour ${unites} unité(s) `
      + `qui valent ${valeur} sous leur barème gelé (§1.2 : paye === valeurDuCarnet(unites))`,
    );
  }
  return valeur;
}

/**
 * CE QU'ON REND QUAND ON RESTITUE k UNITÉS SUR UN CARNET.
 *
 *   rendu(k) = min( paye,                                   ← FILET
 *                   valeurDuCarnet(unites) − valeurDuCarnet(unites − k) )
 *
 * COMPOSABLE PAR CONSTRUCTION, ET C'EST TOUT L'INTÉRÊT. Puisque `paye` vaut
 * `valeurDuCarnet(unites)` avant l'opération et vaudra `valeurDuCarnet(unites
 * − k)` après, la somme de deux restitutions successives se TÉLESCOPE :
 *
 *   (v(n) − v(n−a)) + (v(n−a) − v(n−a−b)) = v(n) − v(n−a−b)
 *
 * Rendre 1 trois fois vaut donc exactement rendre 3, pour toute table et tout
 * découpage. Ce n'est pas une règle ajoutée qu'on peut oublier d'écrire : c'est
 * une somme télescopique, et le banc la balaie sur tous les découpages.
 *
 * ⚠️ CE QUI A ÉTÉ RETIRÉ, ET POURQUOI — LA BORNE AU PRORATA.
 * L'ancienne formule prenait aussi `min(…, floor(paye × k / unites))` pour
 * interdire de « rendre plus que sa part ». Sous l'invariant cette borne est
 * NUISIBLE, et la table du §3.2 le montre en un point : carnet de 11 payé 248
 * (= v(11)), rendre 1 vaut `248 − v(10) = 248 − 225 = 23`, tandis que le
 * prorata plafonne à `floor(248/11) = 22`. UN POINT DE MOINS, et le télescope
 * est cassé — c'est précisément le point où la suite « rendre 1 trois fois »
 * partait en vrille. Le scénario qu'elle protégeait (carnet de dix payé 240
 * dont neuf unités ne valent que 27, table 9 → −90 %) n'EXISTE PLUS : ce carnet
 * est incohérent, et `exigerUnCarnetSain` le refuse en amont. Sous
 * l'invariant, `rendu(1)` y vaut `30 − 27 = 3`, soit exactement un dixième.
 *
 * ⚠️ CE QUI A ÉTÉ RETIRÉ AUSSI — LA BORNE BASSE À ZÉRO.
 * Sur une table non monotone en prix total (§3.1, 10 → −25 % et 12 → −50 %),
 * `v(unites − k)` peut DÉPASSER `v(unites)` : garder neuf unités y coûte plus
 * cher que d'en garder douze. Le rendu est alors NÉGATIF, et c'est la vérité
 * comptable, pas un bug — rapetisser un tel carnet coûte de l'argent.
 * `LUA_PORTER` (§1.6, étapes 3-5) lit déjà `delta = payeCible − paye` dans les
 * deux sens : un rendu négatif est un `delta` positif, donc un débit, et il
 * passe par le même refus de solde insuffisant. Écraser ce signe à zéro
 * RECASSE la composabilité : sur cette table, rendre 2 d'un coup donnerait 0
 * quand rendre 1 deux fois donnerait 23, et la boucle redeviendrait rentable.
 *
 * LE FILET QUI RESTE : `min(paye, …)`. Il ne peut plus mordre tant que
 * l'invariant tient — `v(unites − k) ≥ 0` donc `paye − v(unites − k) ≤ paye` —
 * et le banc le cherche sur tout le domaine plutôt que de le croire. On le
 * garde parce qu'un filet qui ne sert jamais coûte une ligne, alors qu'un
 * rendu supérieur à ce qui a été payé coûterait la cagnotte.
 * Corollaire à ne pas prendre pour un trou de banc : un mutant qui RETIRE ce
 * `Math.min` SURVIT à `essais/zz-economie.mjs`, et c'est exactement ce que le
 * banc affirme — il ne mord nulle part tant que l'invariant tient.
 */
export function renduPour(k, carnet = {}) {
  const { unites = 0, bareme = null } = carnet ?? {};
  const paye = exigerUnCarnetSain(carnet, 'rendu');

  const total = Math.floor(Number(unites));
  if (!Number.isFinite(total) || total <= 0) return 0;

  const rendues = borne(Math.floor(Number(k)) || 0, 0, total);
  if (rendues <= 0) return 0;

  const garde = valeurDuCarnet(total - rendues, bareme);
  return Math.min(paye, paye - garde);
}

/**
 * CE QUE COÛTE DE PORTER UN CARNET À N UNITÉS.
 *
 *   coutPourPorterA(N) = max( 0, valeurDuCarnet(N, bareme) − paye )
 *
 * ⚠️ LE `max(0, …)` N'EST PAS DÉCORATIF. `normaliserPaliers`
 * (`reglages-points.js:192-196`) garantit des REMISES croissantes, pas des PRIX
 * TOTAUX croissants. Avec les paliers 10 → −25 % et 12 → −50 %,
 * `valeur(12) = 180` est inférieur à `valeur(10) = 225` : sans cette borne,
 * ajouter deux unités RENDRAIT 45 points, et la boucle « +2 unités, +45 pts »
 * fabriquerait des points sans fin. Ici ça ne coûte rien et ne rapporte rien ;
 * c'est l'écran du barème qui signale la table incohérente à l'exploitant
 * (§6 · section Santé), parce que c'est là qu'on peut la corriger.
 *
 * Et c'est bien le COÛT qui est borné, pas la reprise : la reprise (§3.2) lit
 * la différence signée `valeurDuCarnet(N) − paye` et rend sa valeur absolue
 * quand elle est négative. Deux lectures du même nombre, chacune à sa place.
 */
export function coutPourPorterA(N, { paye = 0, bareme = null } = {}) {
  const paye0 = Math.max(0, Math.floor(Number(paye)) || 0);
  return Math.max(0, valeurDuCarnet(N, bareme) - paye0);
}

/**
 * CE QUE COÛTE UNE SÉANCE, EN UNITÉS DE CARNET (§1.4).
 *
 *   mode 'duree'   → tranchesPour(seance.minutes, bareme.minutesParTranche)
 *   mode 'episode' → ceil( prix de la NATURE regardée / prix unitaire du carnet )
 *
 * Un film à 60 sur un carnet d'épisodes à 30 coûte DEUX unités. Une séance de
 * 2 h 50 en mode durée à 60 min en coûte TROIS.
 *
 * ⚠️ AUCUNE REMISE N'ENTRE ICI, et c'est la seule chose à retenir de cette
 * fonction. Les remises ont déjà été appliquées à l'ACHAT ; les rejouer à la
 * CONSOMMATION les offrirait deux fois. C'est aussi la parade au « billet au
 * porteur » qui a coulé la conception « Carnet de billets » (F7) : sans elle,
 * dix unités achetées 225 sur un carnet d'épisodes ouvraient dix FILMS, soit
 * 600 points de contenu pour 225, et `prixFilm` devenait un réglage mort.
 *
 * Une séance coûte toujours au moins UNE unité. Un barème à prix nul rendrait
 * `ceil(60 / 0) = Infinity` — un carnet qu'aucune séance ne pourrait plus
 * ouvrir, pour un serveur qui voulait simplement offrir les épisodes.
 */
export function unitesPour(seance = {}, bareme = null) {
  const s = seance && typeof seance === 'object' ? seance : {};
  const r = baremeLisible(bareme);
  const mode = r.mode === 'duree' ? 'duree' : 'episode';

  if (mode === 'duree') return tranchesPour(s.minutes, r.minutesParTranche);

  // ⚠️ TROISIÈME COPIE DE LA RÈGLE C1, RETIRÉE ELLE AUSSI.
  // Cette ligne faisait `entierPositif(r.prixUnitaire, prixUnitaireDuBareme(…))`
  // — soit exactement « l'explicite gagne », réécrit une fois de plus. Et
  // surtout elle ne l'appliquait QU'AU DÉNOMINATEUR : le numérateur repartait
  // du `prixEpisode` brut. Sur le barème du bilan (`prixEpisode: 99`,
  // `prixUnitaire: 25`), un ÉPISODE sur un carnet D'ÉPISODES coûtait
  // `ceil(99 / 25) = 4` unités — le même écart ×4, déplacé de la caisse vers la
  // consommation, où il se serait vu encore plus tard.
  //
  // Les deux prix se lisent donc maintenant dans LE MÊME barème résolu, celui
  // que la caisse facture. La nature du carnet y est tarifée au prix gelé (une
  // unité vaut une unité), les AUTRES natures gardent le leur : F7 tient
  // toujours, un film à 60 sur un carnet à 25 coûte `ceil(60 / 25) = 3`.
  const natureDuCarnet = r.nature === 'film' ? 'film' : 'episode';
  const tarife = baremePourPrix(natureDuCarnet, r);

  const unitaire = prixUnitaireDuBareme(natureDuCarnet, tarife);
  if (unitaire <= 0) return 1;

  const natureVue = s.nature === 'film' ? 'film' : 'episode';
  const regarde = prixUnitaireDuBareme(natureVue, tarife);
  if (!(regarde > 0)) return 1;

  return Math.max(1, Math.ceil(regarde / unitaire));
}

// ---------------------------------------------------------------------------
// LE PRIX D'UN GROUPE — la charge suit les groupes, le prix aussi (22/10)
//
// Proposition validée par Toji (vault : Proposition-Points-Activite-2026-10-22,
// §3 et « Décisions ») : REJOINDRE un groupe en cours est gratuit ; OUVRIR un
// groupe (même seul) coûte
//
//     prix = base × charge × qualité × heure, puis les remises, puis le plancher
//
// Pur, comme le reste du module : on reçoit l'état (groupes ouverts, heure,
// historique), on rend un devis. C'est le lecteur qui débite (`caisse.js`).
//
// ⚠️ LES REMISES SE MULTIPLIENT, ELLES NE S'ADDITIONNENT PAS, ET UN PLANCHER
// LES ARRÊTE. Additionner −30 −20 −20 −15 ferait −85 % ; multiplier fait
// 0,7 × 0,8 × 0,8 × 0,85 = −62 %, et le plancher (défaut : jamais sous 30 % du
// prix de référence) borne ce qu'aucun réglage ne voulait : la gratuité par
// empilement.
// ---------------------------------------------------------------------------

/** Les trois paliers de qualité, du moins cher au plus cher à servir. */
export const QUALITES = Object.freeze(['legere', 'standard', 'haute']);

/**
 * La qualité d'un fichier, d'après son nom (ou son libellé).
 *
 * MESURÉ (proposition §1) : le sortant d'un HEVC DV/HDR ≈ 12 Mbit/s par
 * spectateur, un 1080p x265 ≈ 3-6. « 4K / DV / HDR » est donc `haute`,
 * « 1080p ou moins » `legere`. Un nom muet reste `standard` (×1) : on ne
 * facture pas plus cher ce qu'on ne sait pas.
 */
export function qualiteDuNom(nom) {
  const n = String(nom ?? '');
  const mot = (m) => new RegExp(`(^|[^a-z0-9])(${m})([^a-z0-9]|$)`, 'i').test(n);
  if (mot('2160p|4k|uhd|dv|hdr|hdr10|dovi') || /dolby[ ._-]?vision/i.test(n)) return 'haute';
  if (mot('1080p|720p|576p|480p')) return 'legere';
  return 'standard';
}

/**
 * Le coefficient de charge : 1 jusqu'à la moitié du plafond de groupes, puis
 * linéaire jusqu'à `maxPourcent` (×2 par défaut) AU plafond.
 *
 * `ouverts` compte le groupe qu'on s'apprête à ouvrir : avec un plafond de 5,
 * le 3e groupe paie ×1,2, le 4e ×1,6, le 5e ×2.
 */
export function coefCharge(ouverts, plafond, maxPourcent = 200) {
  const p = Math.max(1, Math.floor(Number(plafond) || 1));
  const r = Math.max(0, Number(ouverts) || 0) / p;
  if (r <= 0.5) return 1;
  const max = Math.max(100, Number(maxPourcent) || 200) / 100;
  return 1 + (max - 1) * Math.min(1, (r - 0.5) / 0.5);
}

/** L'heure de Paris (0-23) d'un instant — le serveur tourne en UTC. */
export function heureDeParis(quand = Date.now()) {
  // ⚠️ `formatToParts`, pas `format` : en fr-FR, `format` rend « 20 h », et `Number('20 h')` vaut NaN.
  const h = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', hour: 'numeric', hourCycle: 'h23' })
    .formatToParts(new Date(quand)).find((p) => p.type === 'hour')?.value;
  return Number(h) % 24;
}

/** Le jour civil de Paris `AAAA-MM-JJ` (une promo finit un jour de Paris). */
export function jourDeParis(quand = Date.now()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(quand));
}

/** `h` est-elle dans [debut, fin[ ? Une plage qui passe minuit (22 → 2) marche aussi. */
export function dansLaPlage(h, debut, fin) {
  const d = Number(debut);
  const f = Number(fin);
  if (d === f) return false;
  return d < f ? h >= d && h < f : (h >= d || h < f);
}

/** Le moment de la journée et son coefficient (heures creuses, pic, sinon 1). */
export function coefHeure(quand, r = {}) {
  const h = heureDeParis(quand);
  if (dansLaPlage(h, r.creusesDebut ?? 2, r.creusesFin ?? 17)) {
    return { moment: 'creuses', coef: (r.coefCreusesPourcent ?? 70) / 100 };
  }
  if (dansLaPlage(h, r.picDebut ?? 20, r.picFin ?? 23)) {
    return { moment: 'pic', coef: (r.coefPicPourcent ?? 120) / 100 };
  }
  return { moment: 'normal', coef: 1 };
}

/** Le coefficient de qualité, en décimal. */
export function coefQualite(qualite, r = {}) {
  if (qualite === 'legere') return (r.coefLegerPourcent ?? 70) / 100;
  if (qualite === 'haute') return (r.coefHautPourcent ?? 150) / 100;
  return 1;
}

/** La remise de fidélité, en pourcent : le plus haut palier atteint en épisodes vus. */
export function remiseFidelite(vus, paliers = []) {
  let meilleure = 0;
  for (const p of paliers ?? []) {
    if ((Number(vus) || 0) >= (Number(p?.vus) || Infinity) && Number(p?.remisePourcent) > meilleure) meilleure = Number(p.remisePourcent);
  }
  return Math.min(100, meilleure);
}

/** La promo en cours sur cette série, ou `null` (le jour de fin compris). */
export function promoDe(serie, promos = [], quand = Date.now()) {
  const jour = jourDeParis(quand);
  return (promos ?? []).find((p) => p?.serie === String(serie ?? '') && String(p.fin ?? '') >= jour) ?? null;
}

/** Les remises (multipliées) puis le plancher, en pourcent de la référence. */
function appliquerRemises(reference, remises, plancherPourcent) {
  let apres = reference;
  for (const x of remises) apres *= 1 - x.pourcent / 100;
  const pp = Number(plancherPourcent);
  const plancher = reference * (Number.isFinite(pp) ? Math.max(0, Math.min(100, pp)) : 30) / 100;
  return { apres: Math.max(apres, plancher), plancherAtteint: apres < plancher };
}

/**
 * LE DEVIS D'OUVERTURE D'UN GROUPE.
 *
 * - `ouverts` : groupes actifs, celui-ci compris ;
 * - `qualite` : 'legere' | 'standard' | 'haute' (inconnue → 'standard') ;
 * - `historique` : { suivant, cache, vus } — épisode suivant d'une série
 *   entamée, contenu servi par un groupe récent, épisodes vus en tout ;
 * - `promo` : la promo de la série (voir `promoDe`), ou `null`.
 *
 * ⚠️ LE PRIX BARRÉ (`reference`) N'INCLUT PAS L'HEURE CREUSE, il inclut le pic.
 * Les heures creuses sont ce que Toji veut ENCOURAGER : elles se lisent donc
 * comme une remise (« heures creuses −30 % »), pas comme un prix de base plus
 * bas qu'on ne remarque pas. Le pic, lui, est un surcoût honnête, et il est
 * dans la référence (pas de faux « prix barré » qui monterait).
 */
export function prixDuGroupe({
  nature = 'episode', reglages = null, ouverts = 1, qualite = 'standard', quand = Date.now(),
  historique = {}, promo = null,
} = {}) {
  const r = reglages ?? {};
  const base = Math.max(0, Math.floor(Number(nature === 'film' ? (r.prixFilm ?? PRIX.film) : (r.prixEpisode ?? PRIX.episode)) || 0));
  const charge = coefCharge(ouverts, r.plafondGroupes ?? 5, r.chargeMaxPourcent ?? 200);
  const q = QUALITES.includes(qualite) ? qualite : 'standard';
  const cQualite = coefQualite(q, r);
  const { moment, coef: cHeure } = coefHeure(quand, r);

  const reference = base * charge * cQualite * Math.max(1, cHeure);
  const remises = [];
  if (cHeure < 1) remises.push({ motif: 'creuses', pourcent: Math.round((1 - cHeure) * 100) });
  if (historique.suivant) remises.push({ motif: 'suivant', pourcent: r.remiseSuivantPourcent ?? 20 });
  if (historique.cache) remises.push({ motif: 'cache', pourcent: r.remiseCachePourcent ?? 20 });
  if (promo?.remisePourcent > 0) remises.push({ motif: 'promo', pourcent: promo.remisePourcent, fin: promo.fin });
  const fid = remiseFidelite(historique.vus, r.fidelite);
  if (fid > 0) remises.push({ motif: 'fidelite', pourcent: fid });
  const actives = remises.filter((x) => x.pourcent > 0);

  const { apres, plancherAtteint } = appliquerRemises(reference, actives, r.plancherPourcent ?? 30);
  // `- 1e-9` : 30 × 0,7 vaut 20,999999… en flottant, et ne doit pas faire 22.
  const prix = base > 0 ? Math.max(1, Math.ceil(apres - 1e-9)) : 0;
  const ref = Math.ceil(reference - 1e-9);
  return {
    prix, reference: ref, economie: Math.max(0, ref - prix), base, nature,
    coefficients: { charge: Math.round(charge * 100) / 100, qualite: cQualite, heure: cHeure },
    qualite: q, moment, remises: actives, plancherAtteint,
  };
}

/**
 * LE DEVIS D'UNE SÉRIE COMPLÈTE : la somme des épisodes à débloquer, moins la
 * remise de série (défaut −30 %), la promo et la fidélité, sous le même
 * plancher. Ni charge ni heure : la série se précharge en heures creuses, elle
 * coûte peu à servir quel que soit le moment où on l'achète.
 */
export function prixDeLaSerie({ episodes = 0, reglages = null, historique = {}, promo = null } = {}) {
  const r = reglages ?? {};
  const n = Math.max(0, Math.floor(Number(episodes) || 0));
  const base = Math.max(0, Math.floor(Number(r.prixEpisode ?? PRIX.episode) || 0));
  const reference = n * base;
  const remises = [{ motif: 'serie', pourcent: r.remiseSeriePourcent ?? 30 }];
  if (promo?.remisePourcent > 0) remises.push({ motif: 'promo', pourcent: promo.remisePourcent, fin: promo.fin });
  const fid = remiseFidelite(historique.vus, r.fidelite);
  if (fid > 0) remises.push({ motif: 'fidelite', pourcent: fid });
  const actives = remises.filter((x) => x.pourcent > 0);
  const { apres, plancherAtteint } = appliquerRemises(reference, actives, r.plancherPourcent ?? 30);
  const prix = reference > 0 ? Math.max(1, Math.ceil(apres - 1e-9)) : 0;
  return { prix, reference, economie: Math.max(0, reference - prix), episodes: n, base, remises: actives, plancherAtteint };
}
